import Phaser from 'phaser';
import { gameState, CONSTANTS } from '../game.js';
import { GameConfig } from '../config/GameConfig.js';
import { UIManager } from '../managers/UIManager.js';
import { client } from '../../../lib/frontend/amplifyClient';
import { SessionManager } from '../utils/SessionManager.js';
import { SubmitIconTexture } from '../textures/SubmitIconTexture.js';

export class GameOverScene extends Phaser.Scene {
    // UIマネージャー
    private uiManager: UIManager | null = null;
    
    // スコア送信状態
    private scoreSubmitted: boolean = false;
    private submitResult: any = null;
    private submitIcon: Phaser.GameObjects.Sprite | null = null;
    private submitAnimation: Phaser.Tweens.Tween | null = null;

    constructor() {
        super({ key: 'GameOverScene' });
    }
    
    preload(): void {
        // スコア送信アイコンのテクスチャを読み込む
        SubmitIconTexture.createAll(this);
    }

    create(): void {
        console.log('GameOverScene: Create function called');
        
        // UIマネージャーの初期化
        this.uiManager = new UIManager(this);
        this.scoreSubmitted = false;
        
        // スコア送信（自動）
        this.submitScore();
        
        // デスクトップの場合のみカーソルをポインタに
        if (!this.game.device.input.touch) {
            this.input.setDefaultCursor('pointer');
        }
        
        // 背景色の設定
        this.cameras.main.setBackgroundColor('#000000');
        
        const screenHeight = this.game.config.height as number;
        // セクションの高さを設定（30%/40%/30%）
        const headerHeight = screenHeight * 0.3;
        const scoreHeight = screenHeight * 0.2;
        const evalHeight = screenHeight * 0.3;
        const footerHeight = screenHeight * 0.2;

        // 上部セクション: ゲームオーバーテキストとスコア
        const gameOverText = this.add.text(
            this.game.config.width as number / 2,
            headerHeight * 0.3,
            'GAME OVER',
            {
                fontSize: '64px',
                fontFamily: 'DotGothic16',
                fill: '#ff0000',
                fontStyle: 'bold',
                stroke: '#ffffff',
                strokeThickness: 6
            } as any
        );
        gameOverText.setOrigin(0.5);

        // スコア情報
        this.createScoreSection(headerHeight * 0.5);

        // 評価セクション（横一列）
        this.createEvaluationSection(headerHeight + scoreHeight);

        // 下部セクション: ボタン
        this.createBottomSection(headerHeight + evalHeight);

        // アニメーション
        this.createAnimations(gameOverText);
        
        // リビジョン情報の表示
        this.uiManager.createRevisionText();
    }

    private createScoreSection(startY: number): void {
        const centerX = this.game.config.width as number / 2;

        // スコアのラベルと値を別々に表示して、数字を大きく
        const scoreLabelText = this.add.text(
            centerX,
            startY + 20,
            'SCORE',
            {
                fontSize: '32px',
                fontFamily: 'DotGothic16',
                fill: '#ffffff',
                fontStyle: 'bold'
            } as any
        );
        scoreLabelText.setOrigin(0.5);

        const scoreValueText = this.add.text(
            centerX,
            startY + 70,
            this.formatScore(gameState.playState.score),
            {
                fontSize: '56px',
                fontFamily: 'DotGothic16',
                fill: '#ffffff',
                fontStyle: 'bold'
            } as any
        );
        scoreValueText.setOrigin(0.5);

        const highScoreLabelText = this.add.text(
            centerX,
            startY + 140,
            'HIGH SCORE',
            {
                fontSize: '32px',
                fontFamily: 'DotGothic16',
                fill: '#ffff00',
                fontStyle: 'bold'
            } as any
        );
        highScoreLabelText.setOrigin(0.5);

        const highScoreValueText = this.add.text(
            centerX,
            startY + 190,
            this.formatScore(gameState.highScore),
            {
                fontSize: '56px',
                fontFamily: 'DotGothic16',
                fill: '#ffff00',
                fontStyle: 'bold'
            } as any
        );
        highScoreValueText.setOrigin(0.5);
    }

    private createEvaluationSection(startY: number): void {
        const centerX = this.game.config.width as number / 2;
        const evaluationColors: Record<string, string> = {
            [CONSTANTS.EVALUATION.TEXT.FAIL]: CONSTANTS.EVALUATION.SETTINGS.FAIL.color,
            [CONSTANTS.EVALUATION.TEXT.PASS]: CONSTANTS.EVALUATION.SETTINGS.PASS.color,
            [CONSTANTS.EVALUATION.TEXT.GOOD]: CONSTANTS.EVALUATION.SETTINGS.GOOD.color,
            [CONSTANTS.EVALUATION.TEXT.GREAT]: CONSTANTS.EVALUATION.SETTINGS.GREAT.color,
            [CONSTANTS.EVALUATION.TEXT.PERFECT]: CONSTANTS.EVALUATION.SETTINGS.PERFECT.color
        };

        // 評価を横一列に配置
        const evaluations = Object.values(CONSTANTS.EVALUATION.TEXT);

        // 画面幅を項目数で均等に分割
        const spacing = 90;
        const totalWidth = spacing * (evaluations.length - 1);
        const startX = centerX - totalWidth / 2;

        evaluations.forEach((evaluation, index) => {
            // 評価テクスチャ
            const evaluationKey = Object.entries(CONSTANTS.EVALUATION.TEXT)
                .find(([key, value]) => value === evaluation)?.[0];
            
            if (!evaluationKey) return;
            
            const textureName = CONSTANTS.EVALUATION.TEXTURE_KEYS[evaluationKey];
            
            const evaluationTexture = this.add.image(
                startX + spacing * index,
                startY + 30,
                textureName
            );
            evaluationTexture.setOrigin(0.5);
            evaluationTexture.setScale(0.4); // サイズ調整

            // 評価数を表示
            const countText = this.add.text(
                startX + spacing * index,
                startY + 70,
                `${gameState.playState.evaluationCounts[evaluationKey]}`,
                {
                    fontSize: '24px',
                    fontFamily: 'DotGothic16',
                    fill: evaluationColors[evaluation],
                    stroke: '#000000',
                    strokeThickness: 2,
                    align: 'center'
                } as any
            );
            countText.setOrigin(0.5);
        });
    }

    private createBottomSection(startY: number): Phaser.GameObjects.GameObject[] {
        const buttonSpacing = 100; // ボタン間の間隔を調整
        
        // リスタートボタン
        const restartButton = this.uiManager?.createButton(
            startY + 50,
            'PLAY AGAIN',
            {
                backgroundColor: '#4CAF50',
                hoverColor: '#45a049',
                fontSize: '42px',
                animate: true
            },
            () => {
                this.resetGameState();
                this.scene.start('GameScene');
            }
        );

        // ランキングボタン
        const rankingButton = this.uiManager?.createButton(
            startY + 50 + buttonSpacing,
            'RANKING',
            {
                backgroundColor: '#FF9800', // オレンジ色
                hoverColor: '#F57C00',
                fontSize: '42px',
                animate: true
            },
            () => {
                // 同じウィンドウでranking.htmlを開く（iOSのポップアップブロック対策）
                window.location.href = './ranking.html';
            }
        );

        // タイトルボタン
        const titleButton = this.uiManager?.createButton(
            startY + 50 + buttonSpacing * 2,
            'BACK TO TITLE',
            {
                backgroundColor: '#2196F3',
                hoverColor: '#1E88E5',
                fontSize: '42px',
                animate: true
            },
            () => {
                this.resetGameState();
                this.scene.start('TitleScene');
            }
        );

        return [restartButton, rankingButton, titleButton].filter(button => button !== undefined) as Phaser.GameObjects.GameObject[];
    }

    /**
     * スコアを表示用にフォーマット
     */
    private formatScore(score: number): string {
        // すべての数値をカンマ区切りで表示
        return score.toLocaleString();
    }

    private resetGameState(): void {
        // プレイ状態をリセット
        gameState.playState.score = 0;
        gameState.playState.gameOver = false;
        gameState.playState.playId = null;
        gameState.playState.maxComboCount = 0;
        gameState.playState.playTime = 0;
        
        // 評価カウントをリセット
        Object.keys(gameState.playState.evaluationCounts).forEach(key => {
            gameState.playState.evaluationCounts[key] = 0;
        });
    }
    
    /**
     * スコア送信処理
     */
    private async submitScore(): Promise<void> {
        if (this.scoreSubmitted) return;
        
        // 送信中アニメーションの開始
        this.startSubmitAnimation();
        
        try {
            // SessionManagerからプレイヤーIDを取得
            const playerId = SessionManager.getPlayerId();
            if (!playerId) {
                console.error('No player ID found, cannot submit score');
                throw new Error('No player ID found');
            }
            
            // スコア送信（自動生成されたmutationを使用）
            const result = await client.mutations.registerScore({
                playerId, // プレイヤーID（SessionManagerから取得）
                playId: gameState.playState.playId || "unknown", // プレイID（ない場合はunknown）
                playerName: gameState.playerName, // gameStateから名前を取得
                score: gameState.playState.score,
                playTime: gameState.playState.playTime || 0, // プレイ時間（秒）
                comboCount: gameState.playState.maxComboCount || 0, // 最大コンボ数
                failCount: gameState.playState.evaluationCounts.FAIL,
                passCount: gameState.playState.evaluationCounts.PASS,
                goodCount: gameState.playState.evaluationCounts.GOOD,
                greatCount: gameState.playState.evaluationCounts.GREAT,
                perfectCount: gameState.playState.evaluationCounts.PERFECT,
                seed: gameState.playState.currentSeed || '',
                version: GameConfig.version, // GameConfigからバージョン情報を取得
            }, {
                authMode: 'identityPool'
            });
            
            console.log('スコア送信成功:', result);
            this.scoreSubmitted = true;
            // Amplify Gen2の自動生成クライアントでは、レスポンスの構造が異なる可能性があるため、
            // 型アサーションを使用して型エラーを回避
            this.submitResult = (result as any).data?.registerScore;
            
            // 送信中アニメーションの停止
            this.stopSubmitAnimation();
            
            // 送信成功メッセージを表示
            this.showSubmitResult(true);
            
        } catch (error) {
            console.error('スコア送信エラー:', error);
            this.scoreSubmitted = true;
            this.submitResult = null;
            
            // 送信中アニメーションの停止
            this.stopSubmitAnimation();
            
            // 送信失敗メッセージを表示
            this.showSubmitResult(false);
        }
    }
    
    /**
     * 送信中アニメーションの開始
     */
    private startSubmitAnimation(): void {
        // スプライトシートからフレームを作成（4フレーム）
        if (!this.anims.exists('submit_sending_anim')) {
            this.anims.create({
                key: 'submit_sending_anim',
                frames: this.anims.generateFrameNumbers('submit_sending', { 
                    start: 0, 
                    end: 3 // 4フレームのアニメーション（0, 1, 2, 3）
                }),
                frameRate: 5, // 1秒間に5フレーム（200ms/フレーム）
                repeat: -1 // 無限に繰り返す
            });
        }
        
        // 右下にスプライトを表示（リビジョン表示の上に配置）
        this.submitIcon = this.add.sprite(
            this.game.config.width as number - 30,
            this.game.config.height as number - 60, // 位置を上に調整
            'submit_sending'
        );
        this.submitIcon.setOrigin(0.5);
        this.submitIcon.setScale(1.0); // スケールを調整
        this.submitIcon.setDepth(30); // Z位置を高く設定（ボタンより前面に表示）
        
        // アニメーション開始
        this.submitIcon.anims.play('submit_sending_anim');
        
        // アニメーション参照を保存（停止用）
        this.submitAnimation = {
            remove: () => {
                if (this.submitIcon) {
                    this.submitIcon.anims.stop();
                }
            }
        } as any;
    }
    
    /**
     * 送信中アニメーションの停止
     */
    private stopSubmitAnimation(): void {
        // アニメーションの停止
        if (this.submitAnimation) {
            this.submitAnimation.remove();
            this.submitAnimation = null;
        }
        
        // アイコンの削除
        if (this.submitIcon) {
            this.submitIcon.destroy();
            this.submitIcon = null;
        }
    }
    
    /**
     * 送信結果表示
     */
    private showSubmitResult(success: boolean): void {
        // 右下にアイコンを表示（リビジョン表示の上に配置）
        const iconKey = success ? 'submit_success' : 'submit_failed';
        const icon = this.add.image(
            this.game.config.width as number - 30,
            this.game.config.height as number - 60, // 位置を上に調整
            iconKey
        );
        icon.setOrigin(0.5);
        icon.setScale(1.0); // スケールを調整
        icon.setDepth(30); // Z位置を高く設定（ボタンより前面に表示）
        
        // アイコンのアニメーション（拡大→元のサイズ）
        this.tweens.add({
            targets: icon,
            scale: { from: 1.5, to: 1.0 },
            duration: 300,
            ease: 'Back.easeOut'
        });
    }

    private createAnimations(gameOverText: Phaser.GameObjects.Text): void {
        // ゲームオーバーテキストのアニメーション
        this.tweens.add({
            targets: gameOverText,
            y: gameOverText.y - 10,
            duration: 1000,
            ease: 'Sine.easeInOut',
            yoyo: true,
            repeat: -1
        });
    }
}
